// New-deal form — bypasses SDB_DataModel2.xlsx by pushing directly to Postgres.
// Loan-officer emails create SharePoint folders (via Power Automate); admin
// pastes the folder URL(s) here + fills in the core fields. Address is
// geocoded via Mapbox so lat/lng/zip/city/state are auto-filled.

import { useMemo, useState } from 'react'
import { useDims } from '../lib/hooks'
import { submitDeal } from '../lib/api'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

// ── Style tokens (match the rest of the dashboard) ────────────────────────
const GOLD  = '#D4AF37'
const NAVY  = '#0A192F'
const NAVY2 = '#061020'
const MUTED = '#94A3B8'
const RED   = '#E74C3C'
const GREEN = '#22C55E'

const labelStyle = { fontSize: 10, letterSpacing: '0.1em', color: MUTED, textTransform: 'uppercase', marginBottom: 4 }
const fieldStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', color: '#FFFFFF', fontSize: 12,
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 4, outline: 'none',
}
// Native <option>/<optgroup> don't inherit parent styles on most browsers — need
// explicit dark background + light text or they render as invisible white-on-white.
const optionStyle = { background: NAVY2, color: '#FFFFFF' }
const sectionStyle = { display: 'flex', flexDirection: 'column', gap: 4 }
const cardStyle = {
  background: NAVY2, border: '1px solid rgba(212,175,55,0.15)', borderRadius: 8,
  padding: 18, display: 'flex', flexDirection: 'column', gap: 14,
}

const LINK_FIELDS = [
  { key: 'link_main_folder',    label: 'Main folder',           hint: 'Root SharePoint folder for this deal' },
  { key: 'link_om',              label: 'OM / Offering memo' },
  { key: 'link_uw_model',        label: 'UW model' },
  { key: 'link_due_diligence',   label: 'Due diligence' },
  { key: 'link_financials',      label: 'Financials' },
  { key: 'link_reit_reports',    label: 'REIT reports' },
  { key: 'link_sources_uses',    label: 'Sources & uses' },
  { key: 'link_third_party_uw',  label: 'Third-party UW' },
]

const INITIAL_FORM = {
  name: '', org: '', address: '',
  zip: '', lat: null, lng: null, city: '', state: '', state_abbr: '',
  // Users pick high-level values; the form resolves them to a concrete
  // status_key / type_key at submit time. Granular stage / property_type can
  // be filled in later by an admin.
  deal_category: '',      // 'acquisition' | 'development' | 'lending'
  status: '',             // 'Open' | 'Won' | 'Lost'
  owner_key: '',
  value: '', nrsf: '', total_units: '', priority: '',
  notes: '',
  link_main_folder: '', link_om: '', link_uw_model: '', link_due_diligence: '',
  link_financials: '', link_reit_reports: '', link_sources_uses: '', link_third_party_uw: '',
}

// Business-facing options — three each, matching the DB's actual distinct values.
const DEAL_CATEGORY_OPTIONS = [
  { value: 'acquisition', label: 'Acquisition' },
  { value: 'development', label: 'Development' },
  { value: 'lending',     label: 'Lending' },
]
const STATUS_OPTIONS = [
  { value: 'Open', label: 'Open' },
  { value: 'Won',  label: 'Won' },
  { value: 'Lost', label: 'Lost' },
]


// Mapbox forward-geocoder — returns {lat, lng, zip, city, state, state_abbr} or null.
async function geocode(address) {
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`
    + `?access_token=${MAPBOX_TOKEN}&types=address,poi,place&limit=5&country=us`
  const res = await fetch(url)
  if (!res.ok) return null
  const data = await res.json()
  return data.features || []
}


function pickComponent(feature, type) {
  // Mapbox v5 `context` is an array of {id: 'postcode.<n>', text: '12345'}
  const ctx = (feature.context || []).find(c => c.id?.startsWith(type + '.'))
  return ctx?.text || null
}


export default function NewDealForm({ onSubmitted }) {
  const { data: dims, isLoading: dimsLoading } = useDims()
  const [form, setForm] = useState(INITIAL_FORM)
  const [suggestions, setSuggestions] = useState([])
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [orgSuggestOpen, setOrgSuggestOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [banner, setBanner] = useState(null)   // {type:'ok'|'err', text}

  // Owner list sorted alphabetically. Status + Deal Type are the 3-option
  // business-facing selectors defined above; the granular dim rows are
  // resolved to keys at submit time via defaultTypeKeyForCategory /
  // defaultStatusKeyForStatus.
  const owners = dims?.owners || []
  const orgs = dims?.orgs || []

  const filteredOrgs = useMemo(() => {
    const q = form.org.trim().toLowerCase()
    if (!q) return []
    return orgs
      .filter(o => o.org_name.toLowerCase().includes(q))
      .slice(0, 12)
  }, [orgs, form.org])
  const orgExactMatch = orgs.some(o => o.org_name.toLowerCase() === form.org.trim().toLowerCase())
  // Pick a sensible default type_key for a chosen deal category (used at submit time).
  // Prefer the row with the fewest specifics (no property_type, no detail) — that's
  // the "plain" row for that category. Falls back to the lowest type_key.
  function defaultTypeKeyForCategory(cat) {
    const rows = (dims?.deal_types || []).filter(dt => dt.deal_category === cat)
    if (!rows.length) return null
    const plain = rows.find(dt => !dt.property_type && !dt.deal_type_detail)
    if (plain) return plain.type_key
    return rows.slice().sort((a, b) => a.type_key - b.type_key)[0].type_key
  }

  // Pick a sensible default status_key for a chosen status. Prefer the earliest
  // stage in the workflow — '1. Screening' for Open, latest stage for Won/Lost.
  function defaultStatusKeyForStatus(status) {
    const rows = (dims?.statuses || []).filter(s => s.status === status)
    if (!rows.length) return null
    // For Open, users are early in the pipeline. For Won/Lost, use the last stage.
    const sorted = rows.slice().sort((a, b) =>
      (a.stage_group || '').localeCompare(b.stage_group || '')
    )
    return status === 'Open' ? sorted[0].status_key : sorted[sorted.length - 1].status_key
  }

  function update(field, value) { setForm(f => ({ ...f, [field]: value })) }

  async function onAddressChange(v) {
    update('address', v)
    if (v.length < 4) { setSuggestions([]); setSuggestOpen(false); return }
    const feats = await geocode(v)
    setSuggestions(feats || [])
    setSuggestOpen(true)
  }

  function pickSuggestion(feature) {
    const [lng, lat] = feature.center || []
    setForm(f => ({
      ...f,
      address:   feature.place_name || f.address,
      lat, lng,
      zip:       pickComponent(feature, 'postcode') || '',
      city:      pickComponent(feature, 'place') || '',
      state:     pickComponent(feature, 'region') || '',
      state_abbr: (feature.context || []).find(c => c.id?.startsWith('region.'))?.short_code?.replace('US-','') || '',
    }))
    setSuggestions([])
    setSuggestOpen(false)
  }

  async function onSubmit(e) {
    e.preventDefault()
    setBanner(null)
    if (!form.name.trim()) { setBanner({ type: 'err', text: 'Deal name is required' }); return }
    if (!form.deal_category || !form.status || !form.owner_key) {
      setBanner({ type: 'err', text: 'Deal Type, Status, and Owner are all required' }); return
    }
    // Resolve high-level selections to concrete FK keys the backend expects.
    const type_key = defaultTypeKeyForCategory(form.deal_category)
    const status_key = defaultStatusKeyForStatus(form.status)
    if (!type_key || !status_key) {
      setBanner({ type: 'err', text: 'Could not resolve deal type / status. Please try again.' }); return
    }
    setSubmitting(true)
    try {
      const { deal_category: _c, status: _s, ...rest } = form
      const payload = {
        ...rest,
        type_key,
        status_key,
        value:       form.value       || null,
        nrsf:        form.nrsf        || null,
        total_units: form.total_units || null,
      }
      const result = await submitDeal(payload)
      setBanner({
        type: 'ok',
        text: result.message
          || `Submitted for review (${result.intake_id}). An admin will approve before it shows on the map.`,
      })
      setForm(INITIAL_FORM)
      if (onSubmitted) onSubmitted(result)
    } catch (err) {
      setBanner({ type: 'err', text: `Failed: ${err.message}` })
    } finally {
      setSubmitting(false)
    }
  }

  if (dimsLoading) {
    return <div style={{ padding: 40, color: MUTED }}>Loading dims…</div>
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', background: NAVY, padding: '24px 28px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>

        <div>
          <h1 style={{ color: '#FFFFFF', fontSize: 20, margin: 0, fontWeight: 600 }}>New Deal</h1>
          <p style={{ color: MUTED, fontSize: 12, margin: '4px 0 0' }}>
            Pushes directly to Postgres. Attachments stay in SharePoint — paste the folder URLs below.
          </p>
        </div>

        {banner && (
          <div style={{
            padding: '10px 14px', borderRadius: 6, fontSize: 12,
            background: banner.type === 'ok' ? 'rgba(34,197,94,0.12)' : 'rgba(231,76,60,0.12)',
            border: `1px solid ${banner.type === 'ok' ? GREEN : RED}`,
            color: banner.type === 'ok' ? GREEN : RED,
          }}>{banner.text}</div>
        )}

        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Core */}
          <div style={cardStyle}>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: '0.15em', fontWeight: 600 }}>CORE</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
              <div style={sectionStyle}>
                <label style={labelStyle}>Deal name *</label>
                <input style={fieldStyle} value={form.name} onChange={e => update('name', e.target.value)} required />
              </div>
              <div style={{ ...sectionStyle, position: 'relative' }}>
                <label style={labelStyle}>Organization / broker</label>
                <input
                  style={fieldStyle}
                  value={form.org}
                  onChange={e => { update('org', e.target.value); setOrgSuggestOpen(true) }}
                  onFocus={() => setOrgSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setOrgSuggestOpen(false), 200)}
                  placeholder="Start typing — pick existing or add new"
                  autoComplete="off"
                />
                {orgSuggestOpen && filteredOrgs.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: NAVY2, border: '1px solid rgba(212,175,55,0.3)', borderRadius: 4,
                    marginTop: 2, maxHeight: 220, overflow: 'auto',
                  }}>
                    {filteredOrgs.map(o => (
                      <div
                        key={o.org_key}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { update('org', o.org_name); setOrgSuggestOpen(false) }}
                        style={{
                          padding: '7px 12px', color: '#FFFFFF', fontSize: 12, cursor: 'pointer',
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                        }}
                      >{o.org_name}</div>
                    ))}
                  </div>
                )}
                {form.org.trim() && !orgExactMatch && (
                  <div style={{ fontSize: 10, color: GOLD, marginTop: 4 }}>
                    New org — will be created as canonical on submit.
                  </div>
                )}
              </div>
            </div>

            <div style={{ ...sectionStyle, position: 'relative' }}>
              <label style={labelStyle}>Address</label>
              <input
                style={fieldStyle}
                value={form.address}
                onChange={e => onAddressChange(e.target.value)}
                onFocus={() => suggestions.length && setSuggestOpen(true)}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 200)}
                placeholder="Start typing an address..."
              />
              {suggestOpen && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  background: NAVY2, border: '1px solid rgba(212,175,55,0.3)', borderRadius: 4,
                  marginTop: 2, maxHeight: 220, overflow: 'auto',
                }}>
                  {suggestions.map(s => (
                    <div key={s.id}
                      onMouseDown={e => { e.preventDefault(); pickSuggestion(s) }}
                      style={{ padding: '8px 12px', color: '#FFFFFF', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      {s.place_name}
                    </div>
                  ))}
                </div>
              )}
              {form.lat && form.lng && (
                <div style={{ fontSize: 10, color: MUTED, marginTop: 4 }}>
                  Geocoded: {form.city && `${form.city}, `}{form.state_abbr || form.state} · {form.zip || 'no ZIP'} · {form.lat.toFixed(4)},{form.lng.toFixed(4)}
                </div>
              )}
            </div>
          </div>

          {/* Classification */}
          <div style={cardStyle}>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: '0.15em', fontWeight: 600 }}>CLASSIFICATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              <div style={sectionStyle}>
                <label style={labelStyle}>Deal type *</label>
                <select style={fieldStyle} value={form.deal_category} onChange={e => update('deal_category', e.target.value)} required>
                  <option value="" style={optionStyle}>— select —</option>
                  {DEAL_CATEGORY_OPTIONS.map(o => (
                    <option key={o.value} value={o.value} style={optionStyle}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div style={sectionStyle}>
                <label style={labelStyle}>Status *</label>
                <select style={fieldStyle} value={form.status} onChange={e => update('status', e.target.value)} required>
                  <option value="" style={optionStyle}>— select —</option>
                  {STATUS_OPTIONS.map(o => (
                    <option key={o.value} value={o.value} style={optionStyle}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div style={sectionStyle}>
                <label style={labelStyle}>Owner *</label>
                <select style={fieldStyle} value={form.owner_key} onChange={e => update('owner_key', e.target.value)} required>
                  <option value="" style={optionStyle}>— select —</option>
                  {owners.map(o => (
                    <option key={o.owner_key} value={o.owner_key} style={optionStyle}>{o.owner}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <div style={sectionStyle}>
                <label style={labelStyle}>Value ($)</label>
                <input style={fieldStyle} type="number" step="0.01" value={form.value} onChange={e => update('value', e.target.value)} />
              </div>
              <div style={sectionStyle}>
                <label style={labelStyle}>NRSF (sqft)</label>
                <input style={fieldStyle} type="number" step="0.01" value={form.nrsf} onChange={e => update('nrsf', e.target.value)} />
              </div>
              <div style={sectionStyle}>
                <label style={labelStyle}>Total units</label>
                <input style={fieldStyle} type="number" step="1" value={form.total_units} onChange={e => update('total_units', e.target.value)} />
              </div>
              <div style={sectionStyle}>
                <label style={labelStyle}>Priority</label>
                <input style={fieldStyle} value={form.priority} onChange={e => update('priority', e.target.value)} placeholder="high / med / low" />
              </div>
            </div>
          </div>

          {/* SharePoint links */}
          <div style={cardStyle}>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: '0.15em', fontWeight: 600 }}>SHAREPOINT LINKS</div>
            <p style={{ color: MUTED, fontSize: 11, margin: '-6px 0 4px' }}>
              Paste the SharePoint URL(s) for this deal. Main folder is what shows on the map sidebar.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {LINK_FIELDS.map(({ key, label, hint }) => (
                <div key={key} style={sectionStyle}>
                  <label style={labelStyle}>{label}</label>
                  <input style={fieldStyle} type="url" placeholder="https://..." value={form[key]} onChange={e => update(key, e.target.value)} />
                  {hint && <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>{hint}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={cardStyle}>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: '0.15em', fontWeight: 600 }}>NOTES</div>
            <textarea
              style={{ ...fieldStyle, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }}
              value={form.notes}
              onChange={e => update('notes', e.target.value)}
              placeholder="Any context from the loan officer email, internal thoughts, etc."
            />
          </div>

          {/* Submit */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingBottom: 40 }}>
            <button
              type="button"
              onClick={() => { setForm(INITIAL_FORM); setBanner(null) }}
              style={{
                padding: '10px 20px', fontSize: 12, letterSpacing: '0.1em',
                background: 'transparent', color: MUTED, border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4, cursor: 'pointer', textTransform: 'uppercase',
              }}
            >Reset</button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '10px 24px', fontSize: 12, letterSpacing: '0.1em',
                background: submitting ? 'rgba(212,175,55,0.5)' : GOLD, color: NAVY,
                border: 'none', borderRadius: 4, cursor: submitting ? 'wait' : 'pointer',
                fontWeight: 600, textTransform: 'uppercase',
              }}
            >{submitting ? 'Saving…' : 'Create deal'}</button>
          </div>

        </form>
      </div>
    </div>
  )
}
